import { forwardRef, useMemo } from "react";

import { constantsData } from "@/data";
import type { AlfredContextIndicatorPropsInterface } from "@/interfaces/alfred";
import { generateValidClassNameHandler } from "@/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/ui/tooltip";

/**
 * Color classes based on severity level
 */
const severityColors = {
    high: {
        bar: "bg-red-500",
        ring: "ring-red-200",
        text: "text-red-600",
    },
    low: {
        bar: "bg-green-500",
        ring: "ring-green-200",
        text: "text-green-600",
    },
    medium: {
        bar: "bg-yellow-500",
        ring: "ring-yellow-200",
        text: "text-yellow-600",
    },
};

/**
 * Size classes for the indicator
 */
const sizeClasses = {
    md: {
        bar: "h-2 w-20",
        container: "h-6 gap-2",
        text: "text-xs",
    },
    sm: {
        bar: "h-1.5 w-16",
        container: "h-5 gap-1.5",
        text: "text-[10px]",
    },
};

/**
 * ContextIndicator - Displays context window usage with visual progress bar
 *
 * Features:
 * - Linear progress bar showing usage percentage
 * - Color-coded by severity (green/yellow/red)
 * - Compact text display (e.g., "12.4k / 128k")
 * - Tooltip with detailed breakdown on hover
 *
 * Usage:
 * ```tsx
 * <ContextIndicator
 *   usedTokens={12400}
 *   maxTokens={128000}
 *   percentage={10}
 *   displayText="12.4k / 128k"
 *   severity="low"
 * />
 * ```
 */
export const AlfredContextIndicator = forwardRef<HTMLDivElement, AlfredContextIndicatorPropsInterface>(
    ({
        className,
        displayText,
        maxOutputTokens,
        percentage,
        severity,
        size = "sm",
        usedTokens,
    }, ref) => {
        const colors = severityColors[severity];
        const sizes = sizeClasses[size];

        // Calculate remaining tokens
        const remainingTokens = useMemo(
            () => Math.max(
                0,
                maxOutputTokens - usedTokens,
            ),
            [maxOutputTokens, usedTokens],
        );

        // Format number with commas for tooltip
        const formatNumber = (num: number) => num.toLocaleString();

        // Tooltip content with detailed breakdown
        const tooltipContent = useMemo(
            () => (
                <div className="space-y-1.5 p-1">
                    <div className="font-medium">{constantsData.msgs.alfred.contextIndicator.contextWindowUsage}</div>
                    <div className="space-y-0.5 text-xs text-gray-600">
                        <div className="flex justify-between gap-4">
                            <span>{constantsData.msgs.alfred.contextIndicator.used}</span>
                            <span className="font-mono">
                                {formatNumber(usedTokens)}
                                {" "}
                                {constantsData.msgs.alfred.contextIndicator.tokens}
                            </span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span>{constantsData.msgs.alfred.contextIndicator.remaining}</span>
                            <span className="font-mono">
                                {formatNumber(remainingTokens)}
                                {" "}
                                {constantsData.msgs.alfred.contextIndicator.tokens}
                            </span>
                        </div>
                        <div className="flex justify-between gap-4">
                            <span>{constantsData.msgs.alfred.contextIndicator.maximum}</span>
                            <span className="font-mono">
                                {formatNumber(maxOutputTokens)}
                                {" "}
                                {constantsData.msgs.alfred.contextIndicator.tokens}
                            </span>
                        </div>
                    </div>
                    <div className="border-t border-gray-100 pt-1">
                        <div className={generateValidClassNameHandler(
                            "text-xs font-medium",
                            colors.text,
                        )}
                        >
                            {percentage}
                            {constantsData.msgs.alfred.contextIndicator.percentUsed}
                            {severity === "high" && constantsData.msgs.alfred.contextIndicator.considerNewConversation}
                        </div>
                    </div>
                </div>
            ),
            [usedTokens, remainingTokens, maxOutputTokens, percentage, severity, colors.text],
        );

        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- tabIndex needed for keyboard accessibility to trigger tooltip */}
                        <div
                            aria-label={`Context usage: ${displayText}, ${percentage}% used`}
                            ref={ref}
                            role="status"
                            tabIndex={0}
                            className={generateValidClassNameHandler(
                                "flex items-center",
                                sizes.container,
                                "cursor-help focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded",
                                className,
                            )}
                        >
                            {/* Progress bar container */}
                            <div
                                className={generateValidClassNameHandler(
                                    "relative rounded-full bg-gray-200 overflow-hidden",
                                    sizes.bar,
                                )}
                            >
                                {/* Progress fill */}
                                <div
                                    className={generateValidClassNameHandler(
                                        "absolute inset-y-0 left-0 rounded-full transition-all duration-300",
                                        colors.bar,
                                    )}
                                    style={{
                                        width: `${Math.min(
                                            percentage,
                                            100,
                                        )}%`,
                                    }}
                                />
                            </div>

                            {/* Text display */}
                            <span
                                className={generateValidClassNameHandler(
                                    "font-mono whitespace-nowrap",
                                    sizes.text,
                                    colors.text,
                                )}
                            >
                                {displayText}
                            </span>
                        </div>
                        {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
                    </TooltipTrigger>
                    <TooltipContent
                        align="end"
                        className="max-w-xs"
                        side="bottom"
                    >
                        {tooltipContent}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    },
);

AlfredContextIndicator.displayName = "AlfredContextIndicator";

// Legacy export for backwards compatibility
export const ContextIndicator = AlfredContextIndicator;
